import Dexie from "dexie";

export const db = new Dexie("novacheck_app");

// Version 45 - Original schema
db.version(45).stores({
    component_groups: "&id",
    components: "&id, &uuid",
    base_components: "&uuid,parent",
    danger_aspects: "&id",
    error_classifications: "&id",
    error_indications: "&id",
    forms: "&id, inspection_id, &uuid",
    forms_location_item_components: "&id, form_id, location_item_component_uuid",
    inspections: "&id, &uuid",
    installation_types: "&id",
    jobs: "id++, &uuid, status, &payload, [module+payload]",
    failed_jobs: "id++, &uuid",
    permanently_failed_jobs: "id++, &uuid",
    location_items: "&uuid, id, location_id,inspection_id,order_id,parent_uuid",
    location_item_components: "&uuid,id, parent_uuid, location_item_uuid, inspection_id",
    location_item_types: "&id",
    location_rotationfields: "&id",
    location_system_types: "&id",
    location_systems: "&uuid,location_systemable_uuid",
    measurement_types: "&id",
    measurement_instruments: "&id",
    measurement_instrument_types: "&id",
    measurements:
        "&uuid,id, location_item_component_uuid, inspection_id,measurement_type_id,[location_item_component_uuid+inspection_id]",
    observations:
        "&uuid,id, location_item_component_uuid, inspection_id,[location_item_component_uuid+inspection_id]",
    remarks: "&id,name",
    repair_periods: "&id",
    user: "&id",
    custom_remarks: "&uuid,&name",
    inspection_method_types: "&id",
    inspection_methods: "&id",
    inspection_statuses: "&id",
    answers: "&id, &uuid, inspection_id,form_id",
    answers_inspections: "&uuid, inspection_id, form_id",
    answers_location_item_components: "&uuid, form_id, location_item_component_uuid",
    documents: "&uuid, documentable_uuid",
    actions: "&uuid, actionable_uuid,inspection_id,status,priority",
    comments: "&uuid, action_uuid",
    notifications: "&id, read_at",
    images: "&uuid, imageable_uuid, s3_key, upload_status, sync_status",
    document_types: "&id",
    regulations: "&id",
    sync_regulations: "id++",
    measurement_type_revisions: "&id, measurement_type_id",
    projects: "&id, &uuid, inspection_id",
    customers: "&id, &uuid, inspection_id",
    locations: "&id, &uuid, inspection_id",
    inspector_users: "&id, inspection_id",
    samples: "&uuid, inspection_id, component_uuid",
    scios_codes: "&uuid, inspection_id",
    task_states: "++id, type, interrupted, timestamp",
});

// Version 46 - Add type index to jobs table for image sync notification queries
db.version(46).stores({
    jobs: "id++, &uuid, type, status, &payload, [module+payload]",
});

// Version 47 - Add type index to failed_jobs and permanently_failed_jobs tables for image sync notification queries
db.version(47).stores({
    failed_jobs: "id++, &uuid, type",
    permanently_failed_jobs: "id++, &uuid, type",
});

// Version 48 - Add S3 upload tracking fields to documents table
db.version(48).stores({
    documents: "&uuid, documentable_uuid, s3_key, upload_status, sync_status",
});

// Version 49 - Add local measurement type revisions and local measurement types
db.version(49).stores({
    local_measurement_type_revisions: "&id, measurement_type_id, parent_revision_id, status, inspection_id, [measurement_type_id+inspection_id]",
    local_measurement_types: "&id, name, status, created_by",
});

// Version 50 - Index `url` on jobs so requests.post() dedup-check works (was throwing SchemaError)
db.version(50).stores({
    jobs: "id++, &uuid, type, status, url, &payload, [module+payload]",
});

// Version 51 - Model forms<->inspections as a real many-to-many.
// A form can be linked to multiple inspections, but the inspection link used to live as
// a single `inspection_id` on the shared `db.forms` catalog row, so a form could only
// appear under one inspection at a time. Move the link to a junction table; `db.forms`
// becomes the pure catalog (drops the inspection_id index). Existing single links are
// backfilled; full multi-links repopulate on the next /optimized sync per inspection.
db.version(51).stores({
    forms: "&id, &uuid",
    forms_inspections: "[form_id+inspection_id], form_id, inspection_id",
}).upgrade(async (tx) => {
    // Backfill from the legacy inspection_id still present on existing form rows.
    // Wrapped so a single malformed row can never throw and block the DB from opening
    // (a thrown upgrade aborts open entirely); links self-heal on the next sync anyway.
    try {
        const forms = await tx.table("forms").toArray();
        const links = legacyFormsToLinks(forms);
        if (links.length) await tx.table("forms_inspections").bulkPut(links);
    } catch (e) {
        console.error("forms_inspections backfill failed (will self-heal on next sync):", e);
    }
});

// Maps legacy `forms` rows (which carried a single inspection_id) to forms_inspections
// junction rows. Exported so the backfill transform is unit-testable without driving a
// full Dexie version upgrade. Only well-formed, positive integer inspection_ids link.
export function legacyFormsToLinks(forms) {
    return forms
        .filter((f) => Number.isInteger(f.inspection_id) && f.inspection_id > 0)
        .map((f) => ({ form_id: f.id, inspection_id: f.inspection_id }));
}
